import React, { useState, useEffect } from "react";
import { useSyncedUser } from "../../context/SyncedUserContext";
import { useNotification } from "../../context/NotificationContext";
import useParking from "../../api/parking";
import ParkingGrid from "./layout/ParkingGrid";
import ParkingLegend from "./layout/ParkingLegend";
import ReserveSpotModal from "./modals/ReserveSpotModal";
import ManageSpotModal from "./modals/ManageSpotModal";
import { MapPin, Info, Calendar, Clock, Bookmark, X, Plus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import AddAvailabilityModal from "./modals/AddAvailabilityModal";
import ReserveParkingModal from "./modals/ReserveParkingModal";
import { Card } from "../ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  ChevronDown,
  ChevronUp,
  Car,
  Truck,
  CircleOff,
  ArrowRight,
} from "lucide-react";
import { useTranslation } from "react-i18next";

const ParkingList = () => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const { syncedUserData } = useSyncedUser();
  const user = syncedUserData?.user;
  const [spots, setSpots] = useState([]);
  const [currentFloor, setCurrentFloor] = useState(1);
  const [selectedSpot, setSelectedSpot] = useState(null);
  const [userReservations, setUserReservations] = useState({});
  const [modalType, setModalType] = useState(null);
  const [confirmationDialog, setConfirmationDialog] = useState({
    isOpen: false,
    reservationId: null,
    pointsCost: null,
  });
  const [isCancelling, setIsCancelling] = useState(false);
  const [availableDates, setAvailableDates] = useState([]);
  const [reservedDates, setReservedDates] = useState([]);
  const [spotReservations, setSpotReservations] = useState([]);
  const [pointTransfers, setPointTransfers] = useState([]);
  const [isFloorSelectorExpanded, setIsFloorSelectorExpanded] = useState(false);
  const [isLegendOpen, setIsLegendOpen] = useState(false);
  const [userSpotCount, setUserSpotCount] = useState(0);
  const [activeUserReservations, setActiveUserReservations] = useState([]);

  const parkingApi = useParking();

  useEffect(() => {
    const fetchSpots = async () => {
      const data = await parkingApi.getAllParkingSpots();
      setSpots(data.spots);

      // Count user's owned spots
      const ownedSpots = data.spots.filter((spot) => spot.owner_id === user.id);
      setUserSpotCount(ownedSpots.length);
    };
    fetchSpots();
  }, [user.id]);

  useEffect(() => {
    const fetchUserReservations = async () => {
      const { reservations } = await parkingApi.getReservations({
        userId: user.id,
      });

      // Filter for active reservations (future dates and not cancelled)
      const now = new Date();
      const active = reservations.filter(
        (res) => new Date(res.end_time) >= now && res.status !== 'cancelled'
      );
      setActiveUserReservations(active);

      // Group reservations by spot ID (excluding cancelled reservations)
      const reservationsBySpot = reservations
        .filter(res => res.status !== 'cancelled')
        .reduce((acc, res) => {
          if (!acc[res.parking_spot_id]) {
            acc[res.parking_spot_id] = [];
          }
          acc[res.parking_spot_id].push(res);
          return acc;
        }, {});
      setUserReservations(reservationsBySpot);
    };
    fetchUserReservations();
  }, [user.id]);

  useEffect(() => {
    const fetchDates = async () => {
      if (selectedSpot) {
        const available = await parkingApi.getAvailableDates(selectedSpot.id);
        const reserved = await parkingApi.getReservedDates(selectedSpot.id);
        setAvailableDates(available);
        setReservedDates(reserved);
      }
    };
    fetchDates();
  }, [selectedSpot]);

  useEffect(() => {
    const fetchSpotData = async () => {
      if (selectedSpot?.owner_id === user.id) {
        const { reservations } = await parkingApi.getReservations({
          spotId: selectedSpot.id,
        });
        const transfers = await parkingApi.getPointTransfers(user.id);
        setSpotReservations(reservations);
        setPointTransfers(transfers);
      }
    };
    fetchSpotData();
  }, [selectedSpot, user.id]);

  const handleSpotClick = (spot) => {
    setSelectedSpot(spot);
    if (spot.owner_id === user.id) {
      setModalType("manage");
    } else if (userReservations[spot.id]?.length > 0) {
      // User has reservations for this spot, open reserve modal to view/manage them
      setModalType("reserve");
    } else if (spot.status === "available" || spot.status === "occupied") {
      setModalType("reserve");
    }
  };

  // Get unique floors from spots
  const floors = [...new Set(spots.map((spot) => spot.floor))].sort();

  // Count available spots on current floor
  const availableSpotsCount = spots.filter(
    (spot) => spot.floor === currentFloor && spot.status === "available"
  ).length;

  const handleReservation = async ({
    spotId,
    startDate,
    endDate,
    totalCost,
  }) => {
    try {
      await parkingApi.createReservation({
        spotId,
        userId: user.id,
        startTime: startDate,
        endTime: endDate,
        pointsCost: totalCost,
      });
      
      // Get the reserved spot details
      const reservedSpot = spots.find(spot => spot.id === spotId);
          const spotName = reservedSpot?.spot_number || t('parking.unknown');
    const floor = reservedSpot?.floor || t('parking.unknown');
      
      // Show success notification with spot details
      showNotification(
        `${t("parking.reservationSuccess")} ${spotName} ${t("parking.onFloor")} ${floor}`,
        "success"
      );
      
      const updatedSpots = await parkingApi.getAllParkingSpots();
      setSpots(updatedSpots.spots);

      // Refresh reservations
      const { reservations } = await parkingApi.getReservations({
        userId: user.id,
      });
      const now = new Date();
      const active = reservations.filter(
        (res) => new Date(res.end_time) >= now && res.status !== 'cancelled'
      );
      setActiveUserReservations(active);

      // Update userReservations for parking grid
      const reservationsBySpot = reservations
        .filter(res => res.status !== 'cancelled')
        .reduce((acc, res) => {
          if (!acc[res.parking_spot_id]) {
            acc[res.parking_spot_id] = [];
          }
          acc[res.parking_spot_id].push(res);
          return acc;
        }, {});
      setUserReservations(reservationsBySpot);

      setSelectedSpot(null);
    } catch (error) {
      console.error(t("parking.errors.createReservation"), error);
      showNotification(t("parking.errors.createReservation"), "warning");
    }
  };

  const handleCancelReservation = async (reservationId, pointsCost) => {
    setConfirmationDialog({
      isOpen: true,
      reservationId,
      pointsCost,
    });
  };

  const confirmCancellation = async () => {
    setIsCancelling(true);
    try {
      await parkingApi.cancelReservation(confirmationDialog.reservationId, user.id);

      // Refresh spots
      const updatedSpots = await parkingApi.getAllParkingSpots();
      setSpots(updatedSpots.spots);

      // Refresh reserved dates
      if (selectedSpot) {
        const reserved = await parkingApi.getReservedDates(selectedSpot.id);
        setReservedDates(reserved);
      }

      // Refresh user reservations
      const { reservations } = await parkingApi.getReservations({
        userId: user.id,
      });
      const now = new Date();
      const active = reservations.filter(
        (res) => new Date(res.end_time) >= now && res.status !== 'cancelled'
      );
      setActiveUserReservations(active);

      // Update userReservations for parking grid
      const reservationsBySpot = reservations
        .filter(res => res.status !== 'cancelled')
        .reduce((acc, res) => {
          if (!acc[res.parking_spot_id]) {
            acc[res.parking_spot_id] = [];
          }
          acc[res.parking_spot_id].push(res);
          return acc;
        }, {});
      setUserReservations(reservationsBySpot);

      // Show success message
      showNotification(t("parking.success.cancelReservation"), "success");

      setConfirmationDialog({
        isOpen: false,
        reservationId: null,
        pointsCost: null,
      });
      setSelectedSpot(null);
      setModalType(null);
    } catch (error) {
      console.error(t("parking.errors.cancelReservation"), error);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleMakeAvailable = async ({ spotId, availableDates }) => {
    try {
      await parkingApi.updateSpotStatus(spotId, "available");
      await parkingApi.setAvailableDates(spotId, availableDates, user.id);
      const updatedSpots = await parkingApi.getAllParkingSpots();
      setSpots(updatedSpots.spots);
      setSelectedSpot(null);
    } catch (error) {
      console.error(t("parking.errors.updateStatus"), error);
    }
  };

  const handleResetStatus = async (spotId) => {
    try {
      await parkingApi.resetSpotStatus(spotId);
      const updatedSpots = await parkingApi.getAllParkingSpots();
      setSpots(updatedSpots.spots);
      setSelectedSpot(null);
    } catch (error) {
      console.error(t("parking.errors.resetStatus"), error);
    }
  };

  const handleCloseModal = () => {
    setSelectedSpot(null);
    setModalType(null);
  };

  // Format date for display
  const formatDate = (dateString) => {
    const options = { weekday: "short", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Format time for display
  const formatTime = (dateString) => {
    const options = { hour: "2-digit", minute: "2-digit" };
    return new Date(dateString).toLocaleTimeString(undefined, options);
  };

  // Mobile floor selector with improved UI
  const MobileFloorSelector = () => (
    <div className="flex flex-col gap-2">
      {/* Floor selector pills - horizontal scrollable */}
      <div className="overflow-x-auto pb-1 -mx-2 px-2">
        <div className="flex gap-1">
          {floors.map((floor) => {
            const floorAvailableCount = spots.filter(
              (spot) => spot.floor === floor && spot.status === "available"
            ).length;

            return (
              <button
                key={floor}
                onClick={() => setCurrentFloor(floor)}
                className={`px-3 py-1.5 rounded-md shrink-0 transition-colors flex items-center gap-1 ${
                  currentFloor === floor
                    ? "bg-primary text-white shadow-sm"
                    : "bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 text-muted-foreground"
                }`}
              >
                <span className="font-medium text-sm">-{floor}</span>
                {floorAvailableCount > 0 && (
                  <Badge
                    variant={currentFloor === floor ? "outline" : "secondary"}
                    className={`ml-0.5 text-xs px-1.5 py-0 h-4 min-w-[1rem] flex items-center justify-center ${
                      currentFloor === floor ? "border-white/40 text-white" : ""
                    }`}
                  >
                    {floorAvailableCount}
                  </Badge>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Legend button and status indicator */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Badge
            variant={availableSpotsCount > 0 ? "success" : "secondary"}
            className="py-0.5 text-xs"
          >
            {availableSpotsCount} {t("parking.floorSelector.available")}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {t("parking.floorSelector.onFloor")} -{currentFloor}
          </span>
        </div>

        <Popover open={isLegendOpen} onOpenChange={setIsLegendOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1 text-xs font-normal text-muted-foreground"
            >
              <Info className="h-3 w-3" />
              <span>{t("parking.legend.title")}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-screen max-w-[280px] p-0"
            align="end"
            side="bottom"
          >
            <div className="p-3 border-b border-neutral-200 dark:border-neutral-700">
              <h3 className="font-medium">{t("parking.legend.title")}</h3>
              <p className="text-xs text-muted-foreground mt-1">
                {t("parking.legend.description")}
              </p>
            </div>
            <div className="p-3 max-h-[40vh] overflow-y-auto">
              <ParkingLegend />
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );

  // Desktop floor selector with improved UI
  const DesktopFloorSelector = () => (
    <div className="flex items-center gap-3 justify-between">
      <div className="flex gap-1">
        <TabsList className="bg-neutral-100 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-0.5">
          {floors.map((floor) => {
            const floorAvailableCount = spots.filter(
              (spot) => spot.floor === floor && spot.status === "available"
            ).length;

            return (
              <TabsTrigger
                key={floor}
                value={floor.toString()}
                onClick={() => setCurrentFloor(floor)}
                className={`relative px-4 py-1.5 data-[state=active]:shadow-none ${
                  currentFloor === floor
                    ? "data-[state=active]:bg-white dark:data-[state=active]:bg-neutral-800"
                    : ""
                }`}
              >
                <div className="flex items-center gap-1.5">
                  <span>-{floor}</span>
                  {floorAvailableCount > 0 && (
                    <Badge
                      variant="secondary"
                      className="text-xs px-1.5 py-0 h-4 min-w-[1rem] flex items-center justify-center"
                    >
                      {floorAvailableCount}
                    </Badge>
                  )}
                </div>
              </TabsTrigger>
            );
          })}
        </TabsList>
      </div>

      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="hidden md:flex h-8">
            <Info className="h-3.5 w-3.5 mr-1.5" />
            <span className="text-sm">{t("parking.legend.title")}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="end">
          <div className="p-3 border-b border-neutral-200 dark:border-neutral-700">
            <h3 className="font-medium">{t("parking.legend.title")}</h3>
            <p className="text-xs text-muted-foreground mt-1">
              {t("parking.legend.description")}
            </p>
          </div>
          <div className="p-3 max-h-96 overflow-y-auto">
            <ParkingLegend />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );

  // User reservations summary with enhanced mobile view
  const UserReservationsSummary = () => {
    if (activeUserReservations.length === 0) return null;

    const nextReservation = [...activeUserReservations].sort(
      (a, b) => new Date(a.start_time) - new Date(b.start_time)
    )[0];

    // Find spot details for this reservation
    const spotDetails =
      spots.find((s) => s.id === nextReservation.parking_spot_id) || {};

    return (
      <Card className="overflow-hidden md:border-primary/20 md:bg-primary/5">
        <div className="bg-primary-50 dark:bg-primary-950/20 py-1.5 px-3 border-b border-primary/20 flex items-center gap-1.5 md:hidden">
          <Bookmark className="h-3.5 w-3.5 text-primary" />
          <span className="text-xs font-medium text-primary">
            {t("parking.reservations.nextReservation")}
          </span>
        </div>
        <div className="p-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div className="flex items-start gap-3 md:items-center">
            <div className="rounded-full bg-primary/10 p-2 hidden md:flex">
              <Calendar className="h-5 w-5 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-1.5 md:hidden mb-1">
                <Calendar className="h-4 w-4 text-primary" />
                <h3 className="font-medium text-sm">
                  {formatDate(nextReservation.start_time)}
                </h3>
              </div>
              <h3 className="font-medium hidden md:block text-base">
                {t("parking.reservations.yourNextReservation")}
              </h3>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className="hidden md:inline">
                  {formatDate(nextReservation.start_time)} -{" "}
                  {formatDate(nextReservation.end_time)}
                </span>
              </div>
            </div>
          </div>

          <div className="text-left md:text-right border-l border-neutral-200 dark:border-neutral-700 pl-3 md:border-none md:pl-0 ml-0.5 md:ml-0">
            <div className="flex items-center gap-1.5 md:hidden">
              <MapPin className="h-3.5 w-3.5 text-primary" />
              <p className="text-sm font-medium">
                {t("parking.spot")}{" "}
                {spotDetails.spot_number || nextReservation.parking_spot_name}
                {spotDetails.floor && (
                  <span className="text-muted-foreground ml-1">
                    ({t("parking.onFloor")} {spotDetails.floor})
                  </span>
                )}
              </p>
            </div>
            <p className="text-sm font-medium hidden md:block">
              {t("parking.spot")}{" "}
              {spotDetails.spot_number || nextReservation.parking_spot_name}
              {spotDetails.floor && (
                <span className="text-muted-foreground ml-1">
                  ({t("parking.onFloor")} {spotDetails.floor})
                </span>
              )}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              -{spotDetails.floor || "?"}
            </p>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="overflow-y-auto max-h-[calc(100dvh)]">
      {/* Title header with integrated floor selector */}
      <div className="px-4 py-3 md:px-6 md:pt-5 md:pb-3 border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 sticky top-0 z-10 shadow-sm">
        <div className="flex items-center justify-between mb-2 md:mb-4">
          <h1 className="text-lg md:text-2xl font-bold flex items-center gap-2">
            <Car className="h-5 w-5 md:h-6 md:w-6 text-primary" />
            <span>{t("parking.title")}</span>

            {userSpotCount > 0 && (
              <Badge variant="outline" className="ml-1.5">
                {t("parking.spotCount", { count: userSpotCount })}
              </Badge>
            )}
          </h1>
        </div>

        <div className="bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20 rounded-lg mb-3 sm:mb-4 overflow-hidden">
          <div className="p-3 sm:p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                  <Car className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm sm:text-base font-semibold text-foreground truncate">
                    {t('parking.additionalParking.title')}
                  </p>
                  <p className="text-xs sm:text-sm text-muted-foreground/80 truncate">
                    {t('parking.additionalParking.description')}
                  </p>
                </div>
              </div>
              <Button
                onClick={() => setModalType("reserveAdd")}
                size="sm"
                className="h-8 sm:h-9 px-3 sm:px-4 bg-primary hover:bg-primary/90 text-white shadow-sm flex-shrink-0"
              >
                <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="ml-1 sm:ml-2 text-xs sm:text-sm font-medium">
                  {t('parking.additionalParking.button')}
                </span>
              </Button>
            </div>
          </div>
        </div>
        {/* Desktop floor selector */}
        <div className="hidden md:block ">
          <Tabs
            defaultValue={currentFloor.toString()}
            onValueChange={(value) => setCurrentFloor(parseInt(value))}
          >
            <DesktopFloorSelector />
          </Tabs>
        </div>
        {/* Mobile floor selector */}
        <div className="md:hidden">
          <MobileFloorSelector />
        </div>
      </div>
      {/* User's next reservation info (if any) */}
      <div className="px-4 md:px-6 mt-3 md:mt-4">
        <UserReservationsSummary />
      </div>

      {/* Parking grid with status indicator */}
      <div className="px-4 md:px-6 mt-3 md:mt-4 pb-16 md:pb-6">
        <div className="flex items-center justify-between mb-1.5 md:mb-2">
          <h2 className="text-sm font-medium flex items-center">
            {t("parking.layout.title")}
          </h2>

          <div className="text-xs text-muted-foreground">
            <span>
              {t("parking.layout.tapToAction", {
                action:
                  availableSpotsCount > 0
                    ? t("parking.actions.reserve")
                    : t("parking.actions.view"),
              })}
            </span>
          </div>
        </div>

        {/* Main parking grid */}
        <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden">
          <div className="p-3 md:p-4">
            <ParkingGrid
              spots={spots}
              currentFloor={currentFloor}
              onSpotClick={handleSpotClick}
              userId={user.id}
              userReservations={userReservations}
            />
          </div>
        </div>
      </div>

      {/* Your existing modals remain unchanged */}
      {modalType === "reserve" && (
        <ReserveSpotModal
          spot={selectedSpot}
          isOpen={!!selectedSpot}
          onClose={() => {
            setSelectedSpot(null);
            setModalType(null);
          }}
          onReserve={handleReservation}
          onCancel={handleCancelReservation}
          availableDates={availableDates}
          reservedDates={reservedDates}
          userId={user.id}
        />
      )}

      {modalType === "manage" && (
        <ManageSpotModal
          spot={selectedSpot}
          isOpen={!!selectedSpot}
          onClose={() => {
            setSelectedSpot(null);
            setModalType(null);
          }}
          reservations={spotReservations}
          transfers={pointTransfers}
          onAddAvailability={() => setModalType("addAvailability")}
        />
      )}

      {modalType === "addAvailability" && (
        <AddAvailabilityModal
          spot={selectedSpot}
          userId={user.id}
          isOpen={true}
          onClose={() => setModalType("manage")}
          onAddAvailability={handleMakeAvailable}
        />
      )}

      {/* Reserve Parking Modal */}
      {modalType === "reserveAdd" && (
        <ReserveParkingModal
          isOpen={true}
          onClose={() => setModalType(null)}
          onReserve={handleReservation}
          userId={user.id}
        />
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmationDialog.isOpen}
        onOpenChange={(isOpen) =>
          setConfirmationDialog((prev) => ({ ...prev, isOpen }))
        }
      >
        <DialogContent className="max-w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("parking.cancelReservation.title")}</DialogTitle>
            <DialogDescription>
              {t("parking.cancelReservation.description", {
                refundPoints: Math.floor(confirmationDialog.pointsCost * 0.95),
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() =>
                setConfirmationDialog({ isOpen: false, reservationId: null })
              }
              disabled={isCancelling}
              className="w-full sm:w-auto"
            >
              {t("parking.cancelReservation.keep")}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmCancellation}
              disabled={isCancelling}
              className="w-full sm:w-auto"
            >
              {isCancelling ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t("parking.cancelReservation.processing")}
                </>
              ) : (
                t("parking.cancelReservation.confirm")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ParkingList;
